import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IMessageTemplate, MessageTemplateService } from '../../core/services/message-template.service';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { AuthService } from '../../core/services/auth';

@Component({
  selector: 'app-message-template',
  imports: [CommonModule, ToastModule, ProgressSpinnerModule],
  templateUrl: './message-template.html',
  styleUrl: './message-template.scss'
})
export class MessageTemplate implements OnInit {
  selectedTemplateId: string | null = null;
  templates: IMessageTemplate[] = [];
  isSaving: boolean = false;
  isLoading: boolean = true;

  constructor(
    private messageTemplateService: MessageTemplateService,
    private messageService: MessageService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this.messageTemplateService.getAllMessageTemplates().subscribe((response: any) => {
      console.log('Raw API response:', response);
      
      // The API returns data in { message: string, data: IMessageTemplate[] } format
      if (response && Array.isArray(response.data)) {
        this.templates = this.filterTemplatesForUser(response.data);
      } else {
        console.error('Unexpected API response format:', response);
        this.templates = [];
      }
      
      console.log('Processed templates:', this.templates);
      
      // Set first template as selected by default if templates exist
      if (this.templates && this.templates.length > 0) {
        this.selectedTemplateId = this.templates[0]._id;
      }
      
      this.isLoading = false;
    }, (error) => {
      console.error('Error fetching templates:', error);
      this.templates = [];
      this.isLoading = false;
    });
  }

  // Filter templates based on user subscription level
  private filterTemplatesForUser(templates: IMessageTemplate[]): IMessageTemplate[] {
    try {
      const currentUser = this.authService.getLoggedInUser();
      const hasSubscriberId = currentUser?.subscriberId?._id || currentUser?.subscriberId;
      
      // If user has subscriberId (subscription user), hide USER_ROLE_APPROVAL template
      // Only show it at global level (users without subscriberId)
      if (hasSubscriberId) {
        // USER_ROLE_APPROVAL template ID from server constants
        const USER_ROLE_APPROVAL_TEMPLATE_ID = '68a6ec1a845b196fe5ccbb3f';
        
        return templates.filter(template => {
          // Filter out templates that match the role approval template ID
          // Check both _id and baseTemplateId in case it's a subscriber override
          const isRoleApprovalTemplate = 
            template._id === USER_ROLE_APPROVAL_TEMPLATE_ID ||
            template.baseTemplateId === USER_ROLE_APPROVAL_TEMPLATE_ID ||
            // Also check by name/trigger pattern as fallback
            (template.name?.toLowerCase().includes('role') && 
             (template.name?.toLowerCase().includes('approval') || template.trigger?.toLowerCase().includes('approval')));
          
          return !isRoleApprovalTemplate;
        });
      }
      
      // Global level users (no subscriberId) can see all templates
      return templates;
    } catch (error) {
      console.error('Error filtering templates:', error);
      // Return all templates if filtering fails
      return templates;
    }
  }

  // Get the currently selected template
  get selectedTemplate(): IMessageTemplate | undefined {
    return this.templates.find(template => template._id === this.selectedTemplateId);
  }

  // Handle template selection
  selectTemplate(templateId: string): void {
    this.selectedTemplateId = templateId;
    // Reset cursor positions when switching templates
    this.lastFocusedField = null;
    this.cursorPositions = {};
  }

  // Check if a template is selected
  isTemplateSelected(templateId: string): boolean {
    return this.selectedTemplateId === templateId;
  }

  // Generate array for content fields based on noOfContent
  get contentFieldsArray(): number[] {
    const template = this.selectedTemplate;
    if (!template || !template.noOfContent || typeof template.noOfContent !== 'number') {
      return [];
    }
    
    // Ensure noOfContent is a positive number
    const count = Math.max(1, Math.floor(template.noOfContent));
    return Array.from({ length: count }, (_, i) => i);
  }

  // Handle saving changes to the selected template
  saveChanges(): void {
    const template = this.selectedTemplate;
    if (template) {
      this.isSaving = true;
      
      this.messageTemplateService.updateMessageTemplate(template._id, template).subscribe({
        next: (response: any) => {
          console.log('Template updated successfully:', response);
          
          // Handle the response based on the API format
          let updatedTemplate: IMessageTemplate;
          if (response && response.data) {
            // If the response has the same format as getAllMessageTemplates
            updatedTemplate = response.data;
          } else {
            // If the response is directly the template object
            updatedTemplate = response;
          }
          
          // Ensure the updated template has the same ID
          if (!updatedTemplate._id) {
            updatedTemplate._id = template._id;
          }
          
          // Update the template in the local array while preserving the selection
          const index = this.templates.findIndex(t => t._id === template._id);
          if (index !== -1) {
            // Store the current selected ID to maintain selection
            const currentSelectedId = this.selectedTemplateId;
            
            // Update the template in the array
            this.templates[index] = { ...updatedTemplate };
            
            // Ensure selection is maintained
            this.selectedTemplateId = currentSelectedId;
            
            // Force change detection by creating a new array reference
            this.templates = [...this.templates];

          // Refresh full list to reflect possible override creation and selection
          this.messageTemplateService.getAllMessageTemplates().subscribe((resp: any) => {
            if (resp && Array.isArray(resp.data)) {
              this.templates = this.filterTemplatesForUser(resp.data);
            }
            // Keep the same template selected if still present, otherwise select first
            if (!this.selectedTemplateId && this.templates.length > 0) {
              this.selectedTemplateId = this.templates[0]._id;
            }
          });
          }
          
          // Show success toast
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Template updated successfully!'
          });
          
          // Reset saving state
          this.isSaving = false;
        },
        error: (error) => {
          console.error('Error updating template:', error);
          
          // Show error toast
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error updating template. Please try again.'
          });
          
          // Reset saving state
          this.isSaving = false;
        }
      });
    }
  }

  // Track cursor positions for text insertion
  private lastFocusedField: 'name' | 'subject' | 'content' | null = null;
  private lastContentIndex: number = 0;
  private cursorPositions: { [key: string]: number } = {};

  // Update template name
  updateTemplateName(name: string): void {
    const template = this.selectedTemplate;
    if (template) {
      template.name = name;
    }
  }

  // Update subject line
  updateSubjectLine(subjectLine: string): void {
    const template = this.selectedTemplate;
    if (template) {
      template.subjectLine = subjectLine;
    }
  }

  // Update specific content field
  updateContent(content: string, index: number): void {
    const template = this.selectedTemplate;
    if (template && template.content) {
      // Ensure the content array has enough elements
      while (template.content.length <= index) {
        template.content.push('');
      }
      template.content[index] = content;
    }
  }

  // Get content value for a specific index
  getContentValue(index: number): string {
    const template = this.selectedTemplate;
    if (template && template.content && template.content[index] !== undefined) {
      return template.content[index];
    }
    return '';
  }

  // Track focus and cursor position
  onFieldFocus(fieldType: 'name' | 'subject' | 'content', event: FocusEvent, contentIndex: number = 0): void {
    this.lastFocusedField = fieldType;
    this.lastContentIndex = contentIndex;
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    const key = fieldType === 'content' ? `${fieldType}_${contentIndex}` : fieldType;
    this.cursorPositions[key] = target.selectionStart || 0;
  }

  // Update cursor position on selection change
  onSelectionChange(fieldType: 'name' | 'subject' | 'content', event: Event, contentIndex: number = 0): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    const key = fieldType === 'content' ? `${fieldType}_${contentIndex}` : fieldType;
    this.cursorPositions[key] = target.selectionStart || 0;
  }

  // Insert variable at cursor position
  insertVariable(variable: string): void {
    if (!this.lastFocusedField || !this.selectedTemplate) {
      return;
    }

    const fieldType = this.lastFocusedField;
    const contentIndex = this.lastContentIndex;
    const key = fieldType === 'content' ? `${fieldType}_${contentIndex}` : fieldType;
    const cursorPos = this.cursorPositions[key] || 0;

    let currentValue = '';
    switch (fieldType) {
      case 'name':
        currentValue = this.selectedTemplate.name || '';
        break;
      case 'subject':
        currentValue = this.selectedTemplate.subjectLine || '';
        break;
      case 'content':
        currentValue = this.getContentValue(contentIndex);
        break;
    }

    // Insert variable at cursor position
    const newValue = currentValue.slice(0, cursorPos) + variable + currentValue.slice(cursorPos);

    // Update the template
    switch (fieldType) {
      case 'name':
        this.updateTemplateName(newValue);
        break;
      case 'subject':
        this.updateSubjectLine(newValue);
        break;
      case 'content':
        this.updateContent(newValue, contentIndex);
        break;
    }

    // Update cursor position to be after the inserted variable
    this.cursorPositions[key] = cursorPos + variable.length;

    // Focus back to the field and set cursor position
    setTimeout(() => {
      let selector = `[data-field="${fieldType}"]`;
      if (fieldType === 'content') {
        selector = `[data-field="${fieldType}_${contentIndex}"]`;
      }
      
      const element = document.querySelector(selector) as HTMLInputElement | HTMLTextAreaElement;
      if (element) {
        element.focus();
        element.setSelectionRange(this.cursorPositions[key], this.cursorPositions[key]);
      }
    }, 0);
  }
}